import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";

/** Fachliche Einordnung einer Rolle (Verantwortlichkeit.kategorie). */
export type RollenKategorie = "LEITUNG" | "GESELLSCHAFTER" | "VERTRETUNG" | "AUFSICHT" | "WEITERE";

/** Eine konkrete Verantwortlichkeit/Rolle aus dem globalen Katalog. */
export interface Verantwortlichkeit {
  id: number;
  bereich: string;
  art: "intern" | "extern";
  bezeichnung: string;
  sortOrder: number;
  aktiv: boolean;
  /** Seit dem Rollenvokabular (08/2026); Altzeilen ohne Wert sind „WEITERE". */
  kategorie?: RollenKategorie;
  /** Stabiler Schlüssel (`GESCHAEFTSFUEHRER`, `KOMMANDITIST`) — nur an Organrollen. */
  schluessel?: string | null;
  rechtsformabhaengig?: boolean;
  istMenschImUnternehmen?: boolean;
  /**
   * Welche Art Quote diese Rolle trägt, wenn keine Rechtsform-Zeile sie
   * überschreibt (PROZENT | EURO | BRUCHTEIL | KEINE). Liegt seit dem
   * Rollenvokabular (202608150003) auf der Leitung — `findAll()` liefert volle
   * Entities —, nur der Web-Typ kannte das Feld bisher nicht.
   */
  quotenTypDefault?: string;
}

/**
 * Eine an einer Rechtsform zulässige Organrolle (GET /verantwortlichkeit/matrix).
 * Die Kategorie kommt aus der MATRIX-Zeile, nicht aus dem Katalog: Dieselbe Rolle
 * liegt je nach Rechtsform in einer anderen Spalte — der Komplementär ist bei der
 * KG Leitung UND Beteiligter.
 */
export interface VerantwortlichkeitMatrixEintrag {
  verantwortlichkeitId: number;
  schluessel: string | null;
  bezeichnung: string;
  bereich: string;
  kategorie: RollenKategorie;
  quotenTyp: string;
  istStandard: boolean;
  istMenschImUnternehmen: boolean;
  sortOrder: number;
}

/**
 * Die an einer Rechtsform zulässigen Organrollen. Ohne Rechtsform und bei den
 * Platzhaltern („Sonstige", „Unbekannt") liefert das Backend bewusst eine leere
 * Liste — dann muss zuerst die Rechtsform erfasst werden (PO 15.08.2026).
 */
export const listVerantwortlichkeitMatrix = async (
  legalFormId: number,
): Promise<VerantwortlichkeitMatrixEintrag[]> => {
  const { data } = await apiFetch<VerantwortlichkeitMatrixEintrag[]>("verantwortlichkeit/matrix", {
    params: { legalFormId },
  });
  return Array.isArray(data) ? data : [];
};

export type VerantwortlichkeitInput = {
  bereich: string;
  art: "intern" | "extern";
  bezeichnung: string;
  sortOrder?: number;
  aktiv?: boolean;
};

export const listVerantwortlichkeiten = async (): Promise<Verantwortlichkeit[]> => {
  const { data } = await apiFetch<Verantwortlichkeit[]>("verantwortlichkeit");
  return Array.isArray(data) ? data : [];
};

async function mutate<T>(path: string, method: "POST" | "PUT" | "DELETE", body?: unknown): Promise<T | null> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = `Fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (method === "DELETE") return null;
  return (await res.json()) as T;
}

export const createVerantwortlichkeit = (input: VerantwortlichkeitInput) =>
  mutate<Verantwortlichkeit>("verantwortlichkeit", "POST", input);

export const updateVerantwortlichkeit = (id: number, input: Partial<VerantwortlichkeitInput>) =>
  mutate<Verantwortlichkeit>(`verantwortlichkeit/${id}`, "PUT", input);

export const deleteVerantwortlichkeit = (id: number) =>
  mutate<void>(`verantwortlichkeit/${id}`, "DELETE");
